import * as XLSX from 'xlsx-js-style'

const formatDate = (value) => {
  if (!value) {
    return '-'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return date.toISOString().slice(0, 10)
}

const formatAmount = (value) => {
  const amount = Number(value)
  if (Number.isNaN(amount)) {
    return '0.00'
  }

  return amount.toFixed(2)
}

const formatStatus = (value) => {
  if (!value) {
    return '-'
  }

  const text = String(value).replaceAll('_', ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const applyTypeColors = (worksheet, rows, columns) => {
  const typeColumnIndex = columns.findIndex(
    (column) => column.id === 'type'
  )

  if (typeColumnIndex === -1) {
    return
  }

  let rowIndex = 1

  rows.forEach(({ expense, rowCount }) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: rowIndex,
      c: typeColumnIndex,
    })

    const cell = worksheet[cellAddress]
    const color = expense.type?.color?.replace('#', '')

    if (cell && color) {
      cell.s = {
        ...(cell.s || {}),
        font: {
          ...(cell.s?.font || {}),
          color: { rgb: color.toUpperCase() },
        },
      }
    }

    rowIndex += rowCount
  })
}

const getExpenseValueByColumnId = (expense, columnId, material = null) => {
  if (!expense || !columnId) {
    return ''
  }

  switch (columnId) {
    case 'supplier':
      return expense.supplier?.name || '-'

    case 'customer':
      return expense.customer || '-'

    case 'material':
      return material?.name || '-'

    case 'quantity':
      return material ? formatAmount(material.pivot?.quantity) : '-'

    case 'unit_price':
      return material
        ? formatAmount(material.pivot?.price ?? material.price)
        : '-'

    case 'amount':
      return formatAmount(expense.amount)

    case 'date':
      return formatDate(expense.date)

    case 'type':
      return expense.type?.name || '-'

    case 'billing_status':
      return formatStatus(expense.billing_status)

    default:
      return ''
  }
}

const applyHeaderStyles = (worksheet, columnCount) => {
  for (let index = 0; index < columnCount; index += 1) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index })

    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = {
        font: {
          bold: true,
        },
      }
    }
  }
}

const applyTableBorders = (
  worksheet,
  rowCount,
  columnCount,
  expenseRows = []
) => {
  const borderColor = { rgb: 'D9D9D9' }

  const border = {
    top: { style: 'thin', color: borderColor },
    bottom: { style: 'thin', color: borderColor },
    left: { style: 'thin', color: borderColor },
    right: { style: 'thin', color: borderColor },
  }

  const materialColumnIndexes = [2, 3, 4, 5] // Material, Quantity, Unit price, Amount

  const internalMaterialRows = new Set()

  expenseRows.forEach(({ startRow, rowCount: groupRowCount }) => {
    for (let row = startRow; row < startRow + groupRowCount - 1; row += 1) {
      internalMaterialRows.add(row)
    }
  })

  for (let row = 0; row < rowCount; row += 1) {
    for (let column = 0; column < columnCount; column += 1) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: column })
      const cell = worksheet[cellAddress]

      if (!cell) continue

      const cellBorder = { ...border }

      // Remove horizontal borders between material rows.
      if (internalMaterialRows.has(row) && materialColumnIndexes.includes(column)) {
        cellBorder.bottom = { style: 'thin', color: { rgb: 'FFFFFF' } }
      }

      if (row > 0 && internalMaterialRows.has(row - 1) && materialColumnIndexes.includes(column)) {
        cellBorder.top = { style: 'thin', color: { rgb: 'FFFFFF' } }
      }

      cell.s = {
        ...(cell.s || {}),
        border: cellBorder,
      }
    }
  }
}

export const generateExpensesExcelExport = (data = [], t, filename) => {
  const columns = [
    { id: 'supplier', header: t('Supplier') },
    { id: 'customer', header: t('Customer') },
    { id: 'material', header: t('Material') },
    { id: 'quantity', header: t('Quantity') },
    { id: 'unit_price', header: t('Unit price') },
    { id: 'amount', header: t('Amount') },
    { id: 'date', header: t('Date') },
    { id: 'type', header: t('Type') },
    { id: 'billing_status', header: t('Billing status') },
  ]

  const mergedColumns = [
    'supplier',
    'customer',
    'date',
    'type',
    'billing_status',
    'amount',
  ]

  const rows = []
  const expenseRows = []
  const merges = []

  let currentRow = 1 // Row 0 contains headers.

  ;(Array.isArray(data) ? data : []).forEach((expense) => {
    const materials = Array.isArray(expense.materials)
      ? expense.materials
      : []

    const items = materials.length ? materials : [null]
    const startRow = currentRow

    items.forEach((material) => {
      const row = columns.reduce((acc, column) => {
        acc[column.header] = getExpenseValueByColumnId(
          expense,
          column.id,
          material
        )

        return acc
      }, {})

      rows.push(row)
      currentRow += 1
    })

    expenseRows.push({
      expense,
      rowCount: items.length,
      startRow,
    })

    // Merge expense-level cells vertically.
    if (items.length > 1) {
      mergedColumns.forEach((columnId) => {
        const columnIndex = columns.findIndex(
          (column) => column.id === columnId
        )

        merges.push({
          s: { r: startRow, c: columnIndex },
          e: { r: currentRow - 1, c: columnIndex },
        })
      })
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: columns.map((column) => column.header),
  })

  worksheet['!merges'] = merges

  // Center vertically aligned merged cells.
  merges.forEach(({ s }) => {
    const cellAddress = XLSX.utils.encode_cell(s)
    const cell = worksheet[cellAddress]

    if (cell) {
      cell.s = {
        ...(cell.s || {}),
        alignment: {
          ...(cell.s?.alignment || {}),
          vertical: 'center',
        },
      }
    }
  })

  // Style header cells.
  applyHeaderStyles(worksheet, columns.length)

  // Apply expense type colors.
  const typeColumnIndex = columns.findIndex(
    (column) => column.id === 'type'
  )

  if (typeColumnIndex !== -1) {
    expenseRows.forEach(({ expense, startRow }) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: startRow,
        c: typeColumnIndex,
      })

      const cell = worksheet[cellAddress]
      const color = expense.type?.color?.replace('#', '')

      if (cell && color) {
        cell.s = {
          ...(cell.s || {}),
          font: {
            ...(cell.s?.font || {}),
            color: { rgb: color.toUpperCase() },
          },
        }
      }
    })
  }

  // Apply borders around the table, rows, and columns.
  applyTableBorders(
    worksheet,
    rows.length + 1,
    columns.length,
    expenseRows
  )

  const workbook = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses')

  XLSX.writeFile(
    workbook,
    `${filename}.xlsx`
  )
}